import { useEffect, useRef } from "react";
import { motionAllowed } from "../../lib/motion.js";
import { Bursts } from "./burst.js";
import { readPalette } from "./palette.js";
import "./sparks.css";

/** What a press can land on that answers it. */
const CONTROLS = [
  "button",
  "a[href]",
  "summary",
  'input[type="checkbox"]',
  'input[type="radio"]',
  '[role="button"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
].join(", ");

/** The rail, and its menus, which open inside it. Nowhere else sparks. */
const SPARKS_IN = ".side";

/** A control named for one of these takes something away, and gets no spark. */
const TAKES_AWAY = /^(delete|remove|revoke|roll|disconnect|pause|sign out)\b/i;

/**
 * Whether a control stays quiet (DESIGN.md §9, "Pixel art", "Drops", "Spark"):
 * one that is off, one that takes something away, anything in a confirm that
 * does, and anything on an error.
 */
function isQuiet(control: Element): boolean {
  if (control.matches(":disabled, [aria-disabled='true'], .button-danger")) return true;
  const name = (control.getAttribute("aria-label") ?? control.textContent).trim();
  if (TAKES_AWAY.test(name)) return true;
  const dialog = control.closest('[role="dialog"], [role="alertdialog"]');
  if (dialog?.querySelector(".button-danger") != null) return true;
  return control.closest(".toast-error, .notice-error") !== null;
}

/** The sky ramp's brightest steps and its tip, read off the page's tokens. */
function sparkColours(): readonly string[] {
  const sky = readPalette(document.documentElement, "sky");
  return [...sky.ramp.slice(6), sky.tip];
}

/**
 * The rail's sparks (DESIGN.md §9, "Pixel art", "Drops", "Spark"): a burst of
 * cells where a control in the rail is pressed — from the pointer, or from the
 * control's right end when a keyboard pressed it. Feedback, not decoration:
 * gone in 600ms, and nothing at all under reduced motion. Only the rail: on
 * every press in the app it was too much (2026-10-13).
 *
 * One canvas over everything, listening to the whole document, so no rail
 * control has to ask for one, and a spark outlives what raised it: a rail row
 * is chosen and the page beside it changes while the cells are still in the air.
 */
export function Sparks() {
  const canvas = useRef<HTMLCanvasElement>(null);

  // The canvas is drawn imperatively, from listeners on the document.
  useEffect(() => {
    const surface = canvas.current;
    const ctx = surface?.getContext("2d") ?? null;
    if (surface === null || ctx === null || !motionAllowed()) return;
    const bursts = new Bursts(surface, ctx);
    const burst = (control: Element, at: { x: number; y: number } | null) => {
      const box = control.getBoundingClientRect();
      const point = at ?? { x: box.right - 14, y: box.top + box.height / 2 };
      bursts.add(point.x, point.y, sparkColours());
    };
    const pressed = (event: MouseEvent) => {
      const control =
        event.target instanceof Element ? event.target.closest(CONTROLS) : null;
      if (control?.closest(SPARKS_IN) == null || isQuiet(control)) return;
      // A keyboard's press has no point; it sparks from the control's end.
      burst(control, event.detail === 0 ? null : { x: event.clientX, y: event.clientY });
    };
    document.addEventListener("click", pressed, true);
    return () => {
      document.removeEventListener("click", pressed, true);
      bursts.stop();
    };
  }, []);

  return <canvas ref={canvas} className="sparks" aria-hidden="true" />;
}
